import { BellIcon, BellOffIcon, PencilIcon, Trash2Icon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { extractStorageResources } from '@campai/lib-common/entities'
import { formatDateTime } from '@campai/lib-common/i18n'
import { Actor, ActorName, Markdown } from '@campai/lib-web/components'
import { Button, Card, CardDescription, CardHeader } from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

import { AttachmentList } from '@/product/community/shared/Attachments'

type TicketData = RouterOutput['product']['feedback']['getTicket']

type HeaderProps = {
    ticket: TicketData
    isFollowUpdating: boolean
    setTicketFollowed: (followed: boolean) => Promise<void>
    canEditTicket: boolean
    setTicketDialogOpen: (open: boolean) => void
    canDeleteTicket: boolean
    setTicketDeleteOpen: (open: boolean) => void
}

export const Header = observer(
    ({
        ticket,
        isFollowUpdating,
        setTicketFollowed,
        canEditTicket,
        setTicketDialogOpen,
        canDeleteTicket,
        setTicketDeleteOpen,
    }: HeaderProps) => {
        const { i18n, t } = useTranslation()
        const ticketCreatedAt = ticket?.createdAt ? formatDateTime(ticket.createdAt, { i18n }) : ''

        return (
            <Card>
                <CardHeader className="gap-4">
                    <div className="flex items-start gap-4">
                        <Actor size="xl" actor={ticket.createdBy!} />
                        <div className="min-w-0 flex-1 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                                        <div className="truncate text-xl font-medium">
                                            <ActorName actor={ticket.createdBy!} />
                                        </div>
                                    </div>
                                    <CardDescription>
                                        {[
                                            ticketCreatedAt,
                                            t('product.feedback.ticket.comments', { count: ticket.commentsCount }),
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </CardDescription>
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        loading={isFollowUpdating}
                                        title={
                                            ticket.followedByCurrentUser
                                                ? t('product.feedback.ticket.unfollow')
                                                : t('product.feedback.ticket.follow')
                                        }
                                        onClick={() => void setTicketFollowed(!ticket.followedByCurrentUser)}
                                    >
                                        {ticket.followedByCurrentUser ? <BellOffIcon /> : <BellIcon />}
                                    </Button>
                                    {canEditTicket && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.feedback.ticket.edit')}
                                            onClick={() => setTicketDialogOpen(true)}
                                        >
                                            <PencilIcon />
                                        </Button>
                                    )}
                                    {canDeleteTicket && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.feedback.ticket.delete')}
                                            onClick={() => setTicketDeleteOpen(true)}
                                        >
                                            <Trash2Icon />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <Markdown className="text-base leading-7" syntaxHighlighting>
                                    {ticket.message}
                                </Markdown>
                                <AttachmentList
                                    markdown={ticket.message}
                                    attachments={extractStorageResources(ticket.attachments)}
                                />
                            </div>
                        </div>
                    </div>
                </CardHeader>
            </Card>
        )
    }
)
